import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Megaphone, GraduationCap } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import LinkifiedText from "./LinkifiedText";
import { Badge } from "@/components/ui/badge";

interface Announcement {
  id: string;
  title: string;
  content: string;
  created_at: string;
  linkedCourses?: { id: string; title: string }[];
}

const HomeAnnouncements = () => {
  const { language } = useLanguage();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: "Latest Announcements",
      viewAll: "View All Announcements",
      noAnnouncements: "No announcements at this time.",
    },
    ru: {
      title: "Последние объявления",
      viewAll: "Посмотреть все объявления",
      noAnnouncements: "В настоящее время объявлений нет.",
    },
    es: {
      title: "Últimos Anuncios",
      viewAll: "Ver Todos los Anuncios",
      noAnnouncements: "No hay anuncios en este momento.",
    },
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [language]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      
      // Fetch latest 3 published announcements
      const { data: announcementsData, error } = await supabase
        .from("announcements")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(3);

      if (error) throw error;

      if (!announcementsData) {
        setAnnouncements([]);
        return;
      }

      // Always fetch translations for the current language
      const { data: translations } = await supabase
        .from("announcements_translations")
        .select("*")
        .eq("language", language)
        .in("announcement_id", announcementsData.map(a => a.id));

      // Fetch linked courses
      const { data: courseLinks } = await supabase
        .from("announcement_courses")
        .select(`
          announcement_id,
          course_id,
          courses!inner(id, title, published)
        `)
        .in("announcement_id", announcementsData.map(a => a.id));

      const translationsMap = new Map(
        translations?.map(t => [t.announcement_id, t]) || []
      );

      // Group courses by announcement
      const coursesMap = new Map<string, { id: string; title: string }[]>();
      courseLinks?.forEach((link: any) => {
        if (link.courses && link.courses.published) {
          if (!coursesMap.has(link.announcement_id)) {
            coursesMap.set(link.announcement_id, []);
          }
          coursesMap.get(link.announcement_id)!.push({
            id: link.courses.id,
            title: link.courses.title,
          });
        }
      });

      const translatedAnnouncements = announcementsData.map(announcement => {
        const translation = translationsMap.get(announcement.id);
        return {
          id: announcement.id,
          title: translation?.title || announcement.title,
          content: translation?.content || announcement.content,
          created_at: announcement.created_at,
          linkedCourses: coursesMap.get(announcement.id) || [],
        };
      });

      setAnnouncements(translatedAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || announcements.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-surface">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Megaphone className="h-8 w-8 text-primary" />
            <h2 className="text-3xl md:text-4xl font-bold">
              {content[language].title}
            </h2>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {announcements.map((announcement) => (
            <Card 
              key={announcement.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => window.location.href = '/announcements'}
            >
              <CardHeader>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Calendar className="h-4 w-4" />
                  <time dateTime={announcement.created_at}>
                    {format(new Date(announcement.created_at), "MMMM d, yyyy")}
                  </time>
                </div>
                <CardTitle className="text-xl">{announcement.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <LinkifiedText 
                  text={announcement.content}
                  className="text-muted-foreground line-clamp-2 whitespace-pre-wrap"
                />
                
                {announcement.linkedCourses && announcement.linkedCourses.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center gap-1 text-xs font-semibold mb-2">
                      <GraduationCap className="h-3 w-3" />
                      {language === 'en' ? 'Related:' :
                       language === 'ru' ? 'Связано:' :
                       'Relacionado:'}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {announcement.linkedCourses.map((course) => (
                        <Badge key={course.id} variant="secondary" className="text-xs">
                          {course.title}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/announcements"
            className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
          >
            {content[language].viewAll} →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeAnnouncements;
